import { memo, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import { createHashHistory } from 'history';
import { useLocation, useParams } from 'react-router-dom';
import Context from 'managers/Context';
import { getLiteral } from 'utils/getLiteral';
import { intercomHideAndShow } from 'utils/intercom';
import { userflowHideAndShow } from 'utils/userflow';

let previousEntityMatch = null;
const DEFAULT_ACCESS_PAGES = {
    1: '/companies',
    2: '/contacts',
    3: '/activities',
    4: '/agenda',
    5: '/documents',
    6: '/sales-orders',
    7: '/opportunities',
    9: '/metrics/reports',
    10: '/products',
    11: '/metrics/sfm',
    12: '/dashboard',
};

export const COOKIE_ROUTER_CURRENT_HASH = 'router-current-hash';
export const hashHistory = createHashHistory();
export const hashStack = [];

export const RouteOnMount = memo(({ title, onMount, element = null }) => {
    const location = useLocation();
    const params = useParams();

    // Forcing re-memo params because useParams returns a new object every time
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoParams = useMemo(() => params, [JSON.stringify(params)]);

    useEffect(() => {
        const path = location.pathname;
        if (!window.location.hash.includes(path)) return;
        if (title && window.location.hash === `#${path}`) document.title = getLiteral(title);

        // Cleaning other entities crud when page jump
        const entityMatch = path?.split('/')[1];
        if (previousEntityMatch && previousEntityMatch !== entityMatch) {
            Context.store.dispatch(Context.actions.EntityActions.closeAll());
        }
        previousEntityMatch = entityMatch;
        onMount && onMount(memoParams);

        window.Intercom && window.Intercom('update');
        intercomHideAndShow();
        userflowHideAndShow();
    }, [location, onMount, memoParams, title]);

    return element;
});

export const getEnvironmentRoute = (route, queryString) => {
    let url = `#${route}`;

    if (queryString) {
        url = `${queryString}${url}`;
    }

    return `/${url}`;
};

export const getRoute = (route, queryString) => {
    if (!route) return;
    let finalRoute = `#${route}`;

    if (queryString) {
        finalRoute = `${queryString}${finalRoute}`;
    }

    return `/${finalRoute}`;
};

export const ensureRoute = (route, queryString) => {
    if (!route) return;
    if (`#${route}` === window.location.hash) return;
    hashHistory.push({
        pathname: route,
        search: queryString,
    });
};

export const backRoute = (id) => {
    const currentRoute = getCurrentPath();
    const segments = currentRoute.split('/').filter(Boolean);

    if (!segments.length) {
        return '/';
    }

    if (!id) {
        const markerIndex = segments.findIndex(
            (segment) => segment === 'edit' || segment === 'new',
        );
        if (markerIndex === -1) {
            return currentRoute;
        }

        return `/${segments.slice(0, markerIndex).join('/')}`;
    }

    const idAsString = String(id);
    const editIndex = segments.indexOf('edit');

    if (editIndex !== -1) {
        const routeIdIndex = segments.indexOf(idAsString, editIndex);
        if (routeIdIndex !== -1) {
            return `/${segments.slice(0, editIndex).join('/')}`;
        }
    }

    const routeIdIndex = segments.indexOf(idAsString);
    if (routeIdIndex !== -1) {
        const nextSegment = segments[routeIdIndex + 1];
        if (nextSegment === 'edit' || nextSegment === 'new') {
            return `/${segments.slice(0, routeIdIndex).join('/')}`;
        }
    }

    const markerIndex = segments.findIndex((segment) => segment === 'edit' || segment === 'new');
    if (markerIndex !== -1) {
        return `/${segments.slice(0, markerIndex).join('/')}`;
    }

    return currentRoute;
};

export const backRouteWorkflow = () => {
    const regexToMatch = new RegExp(`(/workflow).*`);
    const currentRoute = getCurrentPath();
    return currentRoute.replace(regexToMatch, ``);
};

export const EXTERNAL_ROUTES_HASH = [
    '/login',
    '/signup',
    '/logout',
    '/recover-password',
    '/change-password',
    '/expired-password',
    '/contracts-validation',
    '/signature-confirm',
    '/accept-invite',
    '/sage-mobile-invite',
    '/sage-mobile-success',
    '/sage-mobile-error',
    '/error',
    '/onboarding',
    '/dev',
    '/auth',
];

export const isExternalRoute = (hash) => {
    let currentHash = hash || window.location.hash;
    currentHash = currentHash.replace('#', '');
    if (currentHash === '/') return true;
    return EXTERNAL_ROUTES_HASH.find((route) => currentHash.startsWith(route));
};

export const goAfterLogin = () => {
    // From local/web4 history router
    let routerCurrentHash = Cookies.get('router-current-hash');
    if (routerCurrentHash && !isExternalRoute(routerCurrentHash)) {
        Cookies.remove('router-current-hash', {
            domain: getCookieDomainName(window.location.hostname),
        });
        window.location.href = getRoute(routerCurrentHash.replace('#', ''), window.location.search);
        return;
    }

    goDefaultPage();
};

export const goDefaultPage = () => {
    let defaultAccessPage = Context.config?.userData?.defaultAccessPage || '1';
    window.location.href = getRoute(
        DEFAULT_ACCESS_PAGES[defaultAccessPage],
        window.location.search,
    );
};

export const goLogin = () => {
    hashHistory.push({
        pathname: '/login',
    });
};

export const goLogout = () => {
    hashHistory.push({
        pathname: '/logout',
    });
};

export const setCookie = (hash) => {
    Cookies.set(COOKIE_ROUTER_CURRENT_HASH, hash, {
        expires: 31,
        domain: getCookieDomainName(window.location.hostname),
    });
};

export const getCurrentPath = () => window.location.hash?.replace('#', '');

// Global listen
hashHistory.listen(() => {
    const path = window.location.hash;
    if (!isExternalRoute(path)) setCookie(path);
    else intercomHideAndShow('hide');
});

// First time
const firstHash = window.location.hash;
if (firstHash) {
    if (!isExternalRoute(firstHash)) setCookie(firstHash);
}

export function getCookieDomainName(hostName) {
    if (hostName === 'localhost') return hostName;
    return `.${hostName.substring(hostName.lastIndexOf('.', hostName.lastIndexOf('.') - 1) + 1)}`;
}
